import React, { useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Menubar from "../Components/Menubar";
import Totop from "../Components/Totop";
import Footer from "../Components/Footer";
import "../Aboutus/Aboutus.css";
import "./Whoweare.css";
import { ThemeContext } from "../../context";
import checkSquare from "../../assets/images/02about_who_we_are/check-square.svg";
import ceoPhoto from "../../assets/images/02about_herings_team/new200527/1_1nam.png";
import strategyPdf from "../../assets/images/02about_who_we_are/HERINGS_CRO Strategy.pdf";
import { DEFAULT_LANG } from "../../i18n";

const getLangFromPath = (pathname) => {
  const m = pathname.match(/^\/(kr|en)(\/|$)/);
  return m ? m[1] : DEFAULT_LANG;
};

const AREA_ICONS = {
  research: ["M9 3h6", "M10 3v6l-4 8a2 2 0 0 0 2 3h8a2 2 0 0 0 2-3l-4-8V3", "M7.5 15h9"],
  rpm: ["M3 12h4l2 5 4-10 2 5h6", "M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"],
  healthcare: ["M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z", "M10 9h4", "M12 7v4", "M9 15h6"],
};

const AreaIcon = ({ name }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {AREA_ICONS[name].map((d, i) => (
      <path key={i} d={d} />
    ))}
  </svg>
);

const renderMultiline = (text) =>
  text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));

export default function WhoweareP() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);

  const businessAreas = [
    { icon: "rpm", key: "rpm", link: "/rpmsolution" },
    { icon: "healthcare", key: "healthcare", link: "/healthcare" },
    { icon: "research", key: "research", link: "/clinicalresearch" },
  ];

  const services = ["design", "oversight", "analysis"];
  const whyItems = ["design", "oncology", "kol"];
  const caseStudies = t("whoweare.cases.items", { returnObjects: true }) || [];
  const caseRows = ["client", "task", "role", "result", "meaning"];

  // 다른 페이지에서 #cases 등으로 들어오면 해당 섹션으로 이동
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [location.hash]);

  const diamonds = ["identify", "prioritize", "develop", "implement"];

  return (
    <div
      style={{
        backgroundColor: isDark ? "#282828" : undefined,
        color: isDark ? "#fff" : undefined,
      }}
    >
      <Menubar slideIndex={0} />
      <div className="wwa-main-container">
        {/* Hero */}
        <div className="about-section1">
          <div className="ir_text_section1">
            <p className="ir_maintext1">{t("whoweare.hero.title")}</p>
            <span className="ir_subtext1">{t("whoweare.hero.subtitle")}</span>
          </div>
        </div>

        {/* Company Intro */}
        <div
          className="wwa-intro"
          style={{ backgroundColor: isDark ? "#333" : undefined }}
        >
          <p className="wwa-intro-highlight">
            {t("whoweare.intro.highlight")}
          </p>
          <p
            className="wwa-intro-text"
            style={{ color: isDark ? "#ccc" : undefined }}
          >
            {t("whoweare.intro.body")}
          </p>
        </div>

        {/* OUR MISSION */}
        <div
          className="wwa-mission"
          style={{ backgroundColor: isDark ? "#2e2e2e" : undefined }}
        >
          <p
            className="wwa-section-title"
            style={{ color: isDark ? "#fff" : undefined }}
          >
            {t("whoweare.mission.label")} <span>{t("whoweare.mission.highlight")}</span>
          </p>
          <p
            className="wwa-mission-text"
            style={{ color: isDark ? "#ccc" : undefined }}
          >
            {t("whoweare.mission.body")}
          </p>
          <ol className="wwa-steps">
            {diamonds.map((d, i) => (
              <li className="wwa-step" key={d}>
                <span className="wwa-step-no">{`0${i + 1}`}</span>
                <p className="wwa-step-title">
                  {t(`whoweare.mission.diamonds.${d}.title`)}
                </p>
                <p className="wwa-step-desc">{t(`whoweare.mission.cards.${d}`)}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* CEO */}
        <div
          className="wwa-ceo"
          style={{ backgroundColor: isDark ? "#333" : undefined }}
        >
          <p
            className="wwa-section-title"
            style={{ color: isDark ? "#fff" : undefined }}
          >
            <span>{t("whoweare.ceo.title")}</span> {t("whoweare.ceo.highlight")}
          </p>
          <div className="wwa-ceo-content">
            <div className="wwa-ceo-photo">
              <img src={ceoPhoto} alt={t("whoweare.ceo.alt")} className="wwa-ceo-img" />
            </div>
            <div className="wwa-ceo-info">
              <p
                className="wwa-ceo-name"
                style={{ color: isDark ? "#fff" : undefined }}
              >
                {t("whoweare.ceo.name")}
              </p>
              <p className="wwa-ceo-role">{t("whoweare.ceo.role")}</p>
              {["education", "career", "research"].map((sec) => (
                <div className="wwa-ceo-detail" key={sec}>
                  <span
                    className="wwa-ceo-detail-label"
                    style={{ color: isDark ? "#eee" : undefined }}
                  >
                    {t(`whoweare.ceo.${sec}.label`)}
                  </span>
                  <p
                    className="wwa-ceo-detail-text"
                    style={{ color: isDark ? "#aaa" : undefined }}
                  >
                    {renderMultiline(t(`whoweare.ceo.${sec}.text`))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Business Areas */}
        <div
          className="wwa-business"
          style={{ backgroundColor: isDark ? "#2e2e2e" : undefined }}
        >
          <p
            className="wwa-section-title"
            style={{ color: isDark ? "#fff" : undefined }}
          >
            <span>{t("whoweare.business.label")}</span> {t("whoweare.business.highlight")}
          </p>
          <div className="wwa-business-grid">
            {businessAreas.map((item, idx) => (
              <div
                className="wwa-business-card"
                key={idx}
                onClick={() => history.push(`/${lang}${item.link}`)}
                style={{
                  backgroundColor: isDark ? "#3a3a3a" : undefined,
                  borderColor: isDark ? "#555" : undefined,
                }}
              >
                <span className="wwa-business-icon">
                  <AreaIcon name={item.icon} />
                </span>
                <p
                  className="wwa-business-title"
                  style={{ color: isDark ? "#fff" : undefined }}
                >
                  {t(`whoweare.business.areas.${item.key}.title`)}
                </p>
                <p
                  className="wwa-business-desc"
                  style={{ color: isDark ? "#aaa" : undefined }}
                >
                  {t(`whoweare.business.areas.${item.key}.desc`)}
                </p>
                <span className="wwa-business-link">
                  {t("whoweare.business.viewMore")} &rsaquo;
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CRO Services */}
        <div
          className="wwa-services"
          id="services"
          style={{ backgroundColor: isDark ? "#333" : undefined }}
        >
          <p
            className="wwa-section-title"
            style={{ color: isDark ? "#fff" : undefined }}
          >
            <span>{t("whoweare.services.label")}</span> {t("whoweare.services.highlight")}
          </p>
          <div className="wwa-services-lead">
            <p className="wwa-intro-highlight">{t("whoweare.services.lead")}</p>
            <p
              className="wwa-intro-text"
              style={{ color: isDark ? "#ccc" : undefined }}
            >
              {t("whoweare.services.leadBody")}
            </p>
          </div>
          <div className="wwa-services-grid">
            {services.map((key, idx) => (
              <div
                className="wwa-service-card"
                key={key}
                data-no={`0${idx + 1}`}
                style={{
                  backgroundColor: isDark ? "#3a3a3a" : undefined,
                  borderColor: isDark ? "#555" : undefined,
                }}
              >
                <img src={checkSquare} alt="" className="wwa-service-icon" />
                <p
                  className="wwa-service-title"
                  style={{ color: isDark ? "#fff" : undefined }}
                >
                  {t(`whoweare.services.items.${key}.title`)}
                </p>
                <p
                  className="wwa-service-desc"
                  style={{ color: isDark ? "#aaa" : undefined }}
                >
                  {t(`whoweare.services.items.${key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Why HERINGS */}
        <div className="wwa-why">
          <p className="wwa-section-title">
            {t("whoweare.why.label")} <span>{t("whoweare.why.highlight")}</span>
            {t("whoweare.why.labelAfter")}
          </p>
          <div className="wwa-why-list">
            {whyItems.map((key) => (
              <div className="wwa-why-item" key={key}>
                <div className="wwa-why-bullet" />
                <div>
                  <p className="wwa-why-item-title">{t(`whoweare.why.items.${key}.title`)}</p>
                  <p className="wwa-why-item-text">{t(`whoweare.why.items.${key}.text`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Case Studies */}
        <div
          className="wwa-cases"
          id="cases"
          style={{ backgroundColor: isDark ? "#333" : undefined }}
        >
          <p
            className="wwa-section-title"
            style={{ color: isDark ? "#fff" : undefined }}
          >
            <span>{t("whoweare.cases.highlight")}</span> {t("whoweare.cases.title")}
          </p>
          <p className="wwa-cases-subtitle">{t("whoweare.cases.subtitle")}</p>
          <div className="wwa-cases-grid">
            {caseStudies.map((cs, idx) => (
              <div
                className="wwa-case-card"
                key={idx}
                style={{ backgroundColor: isDark ? "#3a3a3a" : undefined }}
              >
                <p className="wwa-case-label">{cs.label}</p>
                <p
                  className="wwa-case-title"
                  style={{ color: isDark ? "#fff" : undefined }}
                >
                  {cs.title}
                </p>
                <div className="wwa-case-details">
                  {caseRows
                    .filter((row) => cs[row])
                    .map((row) => (
                      <div className="wwa-case-row" key={row}>
                        <span
                          className="wwa-case-row-label"
                          style={{ color: isDark ? "#eee" : undefined }}
                        >
                          {t(`whoweare.cases.rowLabels.${row}`)}
                        </span>
                        <span
                          className="wwa-case-row-text"
                          style={{ color: isDark ? "#aaa" : undefined }}
                        >
                          {cs[row]}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA / Download */}
        <div className="wwa-cta">
          <p className="wwa-cta-title">{t("whoweare.cta.title")}</p>
          <p className="wwa-cta-subtitle">{t("whoweare.cta.subtitle")}</p>
          <div className="wwa-cta-buttons">
            <a
              href={strategyPdf}
              download="HERINGS_CRO_Strategy.pdf"
              className="wwa-btn-download"
            >
              {t("whoweare.cta.downloadBtn")}
            </a>
            <a
              href="mailto:herings@heringsglobal.com"
              className="wwa-btn-contact"
            >
              {t("whoweare.cta.contactBtn")}
            </a>
          </div>
        </div>

        <Totop />
        <Footer />
      </div>
    </div>
  );
}
